import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  IconButton,
  Chip,
  Fade,
  Zoom,
} from "@mui/material";
import {
  ArrowForward,
  CheckCircle,
  LocalShipping,
  Engineering,
  Security,
  Speed,
  Verified,
  Phone,
  Email,
  WhatsApp,
  KeyboardArrowDown,
  Handshake,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCompany } from "../hooks/useCompany";

// Professional styled components
const HeroSection = styled(Box)`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 20% 50%,
      rgba(211, 47, 47, 0.1) 0%,
      transparent 50%
    );
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 80% 50%,
      rgba(211, 47, 47, 0.05) 0%,
      transparent 50%
    );
  }
`;

const ScrollIndicator = styled(Box)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }
`;

const FeatureCard = styled(Card)`
  height: 100%;
  background: white;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #d32f2f, #ff6b6b);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    border-color: transparent;

    &::before {
      transform: scaleX(1);
    }
  }
`;

const ServiceCard = styled(Box)`
  padding: 40px;
  background: white;
  border-radius: 16px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;

  &:hover {
    border-color: #d32f2f;
    transform: translateY(-4px);
    box-shadow: 0 16px 48px rgba(211, 47, 47, 0.08);
  }
`;

const ReferenceCard = styled(Box)`
  background: white;
  border-radius: 16px;
  padding: 28px 24px;
  height: 100%;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px solid #eee;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #d32f2f, #ff5252);
    transform: scaleX(0);
    transition: transform 0.35s ease;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(211, 47, 47, 0.12);
    border-color: rgba(211, 47, 47, 0.2);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const CTASection = styled(Box)`
  background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
  padding: 80px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
  }
`;

const Home: React.FC = () => {
  const { t } = useTranslation();
  const company = useCompany();
  const [visible, setVisible] = React.useState(false);
  const [references, setReferences] = React.useState<
    { name: string; company: string; logo?: string }[]
  >([
    { name: "Ahmet Yılmaz", company: "Yılmaz İnşaat" },
    { name: "Mehmet Kaya", company: "Kaya Mimarlık" },
    { name: "Ali Demir", company: "Demir Yapı" },
  ]);
  const sliderRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setVisible(true);
  }, []);

  React.useEffect(() => {
    fetch("/data/company/references.json", { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => {
        if (Array.isArray(json?.items)) {
          setReferences(json.items);
        }
      })
      .catch(() => {
        // ignore and keep defaults
      });
  }, []);

  const features = [
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: t("home.features.maxSecurity.title"),
      description: t("home.features.maxSecurity.desc"),
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: t("home.features.fastDelivery.title"),
      description: t("home.features.fastDelivery.desc"),
    },
    {
      icon: <Engineering sx={{ fontSize: 40 }} />,
      title: t("home.features.proInstall.title"),
      description: t("home.features.proInstall.desc"),
    },
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: t("home.features.warrantySupport.title"),
      description: t("home.features.warrantySupport.desc"),
    },
  ];

  const services = [
    {
      icon: <LocalShipping sx={{ fontSize: 48, color: "#d32f2f" }} />,
      title: t("home.services.items.freeSurvey.title"),
      description: t("home.services.items.freeSurvey.desc"),
    },
    {
      icon: <Engineering sx={{ fontSize: 48, color: "#d32f2f" }} />,
      title: t("home.services.items.projectConsulting.title"),
      description: t("home.services.items.projectConsulting.desc"),
    },
    {
      icon: <Handshake sx={{ fontSize: 48, color: "#d32f2f" }} />,
      title: t("home.services.items.corporateSolutions.title"),
      description: t("home.services.items.corporateSolutions.desc"),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={visible} timeout={1000}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Chip
                  label={t("home.heroChip")}
                  sx={{
                    mb: 3,
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    color: "#ff6b6b",
                    border: "1px solid rgba(211, 47, 47, 0.2)",
                    fontWeight: 600,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    color: "white",
                    fontWeight: 800,
                    fontSize: { xs: "2.5rem", md: "4rem" },
                    lineHeight: 1.1,
                    mb: 3,
                  }}
                >
                  {t("hero.title")}
                  <Box
                    component="span"
                    sx={{ color: "#ff6b6b", display: "block" }}
                  >
                    {t("hero.subtitle")}
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  {t("products.subtitle")}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mb: 4 }}
                >
                  <Button
                    component={RouterLink}
                    to="/products"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      backgroundColor: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "#c62828",
                      },
                    }}
                  >
                    {t("hero.cta")}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      color: "white",
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    {t("home.ctaButton")}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
                  {[
                    { icon: <CheckCircle />, text: t("home.badges.warranty") },
                    {
                      icon: <LocalShipping />,
                      text: t("home.badges.freeShipping"),
                    },
                    { icon: <Phone />, text: t("home.badges.support") },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box sx={{ color: "#ff6b6b" }}>{item.icon}</Box>
                      <Typography sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    position: "relative",
                    height: { xs: 300, md: 500 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src="/uploads/AREL.png"
                    alt="Yanmaz Kapı"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 4,
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                      background: "#111",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
        <ScrollIndicator>
          <IconButton sx={{ color: "white" }}>
            <KeyboardArrowDown sx={{ fontSize: 32 }} />
          </IconButton>
        </ScrollIndicator>
      </HeroSection>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#d32f2f",
              fontWeight: 600,
              letterSpacing: 2,
              fontSize: "0.9rem",
            }}
          >
            {t("home.overlineWhy")}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2 }}>
            {t("home.featuresTitle")}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto" }}
          >
            {t("home.featuresSubtitle")}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid key={index} item xs={12} sm={6} md={3}>
              <Zoom
                in={visible}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <FeatureCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ color: "#d32f2f", mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#d32f2f",
              fontWeight: 600,
              letterSpacing: 2,
              fontSize: "0.9rem",
            }}
          >
            {t("home.servicesOverline")}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
            {t("home.servicesTitle")}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid key={index} item xs={12} md={4}>
              <ServiceCard>
                {service.icon}
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 2 }}>
                  {service.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  {service.description}
                </Typography>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* References Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "-5%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(211,47,47,0.04) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "5%",
            right: "-5%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(211,47,47,0.03) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: "#d32f2f",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: "0.85rem",
                display: "inline-block",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 40,
                  height: 2,
                  bgcolor: "#d32f2f",
                  borderRadius: 1,
                },
              }}
            >
              {t("home.testimonialsOverline")}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mt: 3,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                background: "linear-gradient(135deg, #1a1a1a 0%, #444 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("home.testimonialsTitle")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mt: 2,
                maxWidth: 500,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              Türkiye'nin önde gelen inşaat ve mimarlık firmalarıyla çalışmaktan
              gurur duyuyoruz.
            </Typography>
          </Box>

          <Box sx={{ position: "relative" }}>
            {/* Left Arrow */}
            <IconButton
              onClick={() => {
                const el = sliderRef.current;
                if (!el) return;
                const scrollAmount = el.clientWidth * 0.8;
                el.scrollBy({ left: -scrollAmount, behavior: "smooth" });
              }}
              sx={{
                position: "absolute",
                top: "50%",
                left: { xs: -8, md: -24 },
                transform: "translateY(-50%)",
                bgcolor: "white",
                width: 48,
                height: 48,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                zIndex: 2,
                display: { xs: "none", sm: "flex" },
                border: "1px solid #eee",
                "&:hover": {
                  bgcolor: "#d32f2f",
                  color: "white",
                  borderColor: "#d32f2f",
                },
                transition: "all 0.25s ease",
              }}
            >
              <ChevronLeft />
            </IconButton>

            {/* Slider */}
            <Box
              ref={sliderRef}
              sx={{
                display: "flex",
                gap: { xs: 2, md: 3 },
                overflowX: "auto",
                scrollBehavior: "smooth",
                px: { xs: 1, md: 3 },
                py: 3,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {references.map((ref, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: { xs: "0 0 200px", sm: "0 0 240px", md: "0 0 280px" },
                  }}
                >
                  <ReferenceCard>
                    {/* Logo or Icon */}
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "16px",
                        bgcolor: ref.logo
                          ? "white"
                          : "rgba(211, 47, 47, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        border: ref.logo ? "1px solid #eee" : "none",
                        overflow: "hidden",
                      }}
                    >
                      {ref.logo ? (
                        <Box
                          component="img"
                          src={ref.logo}
                          alt={ref.company}
                          sx={{
                            width: 56,
                            height: 56,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Handshake
                          sx={{ fontSize: 32, color: "#d32f2f", opacity: 0.8 }}
                        />
                      )}
                    </Box>

                    {/* Company Name */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: { xs: "0.95rem", md: "1.05rem" },
                        lineHeight: 1.3,
                      }}
                    >
                      {ref.company}
                    </Typography>

                    {/* Contact Person */}
                    {ref.name && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mt: 0.5,
                          fontSize: "0.85rem",
                        }}
                      >
                        {ref.name}
                      </Typography>
                    )}
                  </ReferenceCard>
                </Box>
              ))}
            </Box>

            {/* Right Arrow */}
            <IconButton
              onClick={() => {
                const el = sliderRef.current;
                if (!el) return;
                const scrollAmount = el.clientWidth * 0.8;
                el.scrollBy({ left: scrollAmount, behavior: "smooth" });
              }}
              sx={{
                position: "absolute",
                top: "50%",
                right: { xs: -8, md: -24 },
                transform: "translateY(-50%)",
                bgcolor: "white",
                width: 48,
                height: 48,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                zIndex: 2,
                display: { xs: "none", sm: "flex" },
                border: "1px solid #eee",
                "&:hover": {
                  bgcolor: "#d32f2f",
                  color: "white",
                  borderColor: "#d32f2f",
                },
                transition: "all 0.25s ease",
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  mb: 2,
                }}
              >
                {t("home.ctaTitle")}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  mb: 3,
                }}
              >
                {t("home.ctaSubtitle")}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <Phone />
                </IconButton>
                <IconButton
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <WhatsApp />
                </IconButton>
                <IconButton
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <Email />
                </IconButton>
                <Typography sx={{ color: "white", ml: 2 }}>
                  {company?.phone || "+90 552 837 83 77"}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  backgroundColor: "white",
                  color: "#d32f2f",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                {t("home.ctaButton")}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </CTASection>
    </Box>
  );
};

export default Home;
